import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Car,
  PlusCircle,
  Wallet,
  ShieldCheck,
  Users,
  FileText,
  LogOut,
} from "lucide-react";

const links = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/inventory", label: "Stock & Search", icon: Car },
  { to: "/intake", label: "Vehicle Entry", icon: PlusCircle },
  { to: "/cashier", label: "Night Cashier", icon: Wallet },
  { to: "/mediators", label: "Mediators", icon: Users },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/audit", label: "Audit Sheet", icon: ShieldCheck, ownerOnly: true },
];

export default function AppLayout() {
  const { user, logout, isOwner } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const visible = links.filter((l) => !l.ownerOnly || isOwner);

  const NavItem = ({ l, mobile }) => (
    <NavLink
      key={l.to}
      to={l.to}
      end={l.end}
      data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl font-bold transition-colors ${
          mobile ? "flex-col text-[11px] px-2 py-1.5 gap-1" : "px-4 py-3 text-[15px]"
        } ${
          isActive
            ? "bg-[#1E3A8A] text-white"
            : "text-slate-600 hover:bg-slate-100"
        }`
      }
    >
      <l.icon className={mobile ? "w-6 h-6" : "w-6 h-6"} strokeWidth={2.5} />
      <span className={mobile ? "" : ""}>{l.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-white flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col border-r border-slate-200 bg-white p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-2 py-3 mb-4">
          <img src="/logo.png" alt="A1 Cars" className="w-12 h-12 rounded-full border border-slate-200" />
          <div>
            <div className="font-head font-black text-xl leading-none tracking-tight text-[#0F172A]">A1 CARS</div>
            <div className="text-xs font-bold text-slate-400 tracking-widest">SINCE 1981</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1.5">
          {visible.map((l) => (
            <NavItem key={l.to} l={l} />
          ))}
        </nav>
        <div className="mt-auto pt-4 border-t border-slate-200">
          <div className="px-2 mb-3">
            <div className="font-bold text-[#0F172A]">{user?.name}</div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#EA580C]">{user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout} className="a1-btn a1-btn-ghost w-full !h-12 !text-base">
            <LogOut className="w-5 h-5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white z-20">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="A1" className="w-9 h-9 rounded-full" />
            <span className="font-head font-black text-lg tracking-tight">A1 CARS</span>
          </div>
          <button data-testid="logout-btn-mobile" onClick={handleLogout} className="text-slate-500">
            <LogOut className="w-6 h-6" />
          </button>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-28 lg:pb-8">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 flex justify-around py-1.5 z-20">
          {visible.slice(0, 5).map((l) => (
            <NavItem key={l.to} l={l} mobile />
          ))}
        </nav>
      </div>
    </div>
  );
}
